/**
 * Chart Instance Hook
 * Manages uPlot chart lifecycle and configuration
 */

import { useEffect, useRef, useCallback } from 'react';
import uPlot from 'uplot';
import { ProcessedChartData } from '../types';
import { CHART_HEIGHT } from '../constants';
import { formatAxisTime, formatNumber } from '../utils';
import {
  wheelZoomPlugin,
  touchZoomPlugin,
  dragSelectPlugin,
  adaptivePointsPlugin
} from '../plugins';

interface UseChartInstanceProps {
  containerRef: React.RefObject<HTMLDivElement | null>;
  processedData: ProcessedChartData;
  resolvedValueKey: string | undefined;
  unit: string | undefined;
  yDomain: [number | "auto", number | "auto"];
  color: string;
  pointSize: number;
  showPointsRef: React.MutableRefObject<boolean>;
  showPointsEnabledRef: React.MutableRefObject<boolean>;
}

export function useChartInstance({
  containerRef,
  processedData,
  resolvedValueKey,
  unit,
  yDomain,
  color,
  pointSize,
  showPointsRef,
  showPointsEnabledRef
}: UseChartInstanceProps) {
  const chartRef = useRef<uPlot | null>(null);
  const processedDataRef = useRef(processedData);
  processedDataRef.current = processedData;

  // Reset zoom function
  const handleResetZoom = useCallback(() => {
    if (chartRef.current && processedDataRef.current.timestamps.length > 0) {
      const { timestamps } = processedDataRef.current;
      chartRef.current.setScale("x", {
        min: timestamps[0],
        max: timestamps[timestamps.length - 1],
      });
    }
  }, []);

  // Create/update chart
  useEffect(() => {
    if (!containerRef.current || processedData.validCount === 0) return;

    const { timestamps, values } = processedData;
    const container = containerRef.current;

    // Destroy existing chart
    if (chartRef.current) {
      chartRef.current.destroy();
      chartRef.current = null;
    }

    // Clear container
    container.innerHTML = "";

    // Calculate Y domain with padding
    let calcYMin = yDomain[0] === "auto" ? processedData.yMin : yDomain[0];
    let calcYMax = yDomain[1] === "auto" ? processedData.yMax : yDomain[1];

    if (yDomain[0] === "auto" || yDomain[1] === "auto") {
      const padding = (calcYMax - calcYMin) * 0.05;
      if (yDomain[0] === "auto") calcYMin -= padding;
      if (yDomain[1] === "auto") calcYMax += padding;
    }

    const valueLabel = unit ? `${resolvedValueKey} (${unit})` : resolvedValueKey;

    // Create plugins
    const plugins = [
      wheelZoomPlugin({ factor: 0.75 }),
      touchZoomPlugin(),
      dragSelectPlugin(),
      adaptivePointsPlugin(showPointsRef, showPointsEnabledRef),
    ];

    const opts: uPlot.Options = {
      width: container.clientWidth,
      height: CHART_HEIGHT,
      plugins,
      cursor: {
        drag: { x: true, y: false, uni: 50 },
        sync: {
          key: "dataChart",
        },
      },
      scales: {
        x: {
          time: true,
          min: timestamps[0],
          max: timestamps[timestamps.length - 1],
        },
        y: {
          range: (_u, dataMin, dataMax) => {
            // Fixed (non-auto) yDomain wins over autofit.
            if (yDomain[0] !== "auto" && yDomain[1] !== "auto") {
              return [calcYMin, calcYMax];
            }
            if (!Number.isFinite(dataMin) || !Number.isFinite(dataMax)) {
              return [calcYMin, calcYMax];
            }
            if (dataMin === dataMax) {
              return [dataMin - 1, dataMax + 1];
            }
            const padding = (dataMax - dataMin) * 0.05;
            return [dataMin - padding, dataMax + padding];
          },
        },
      },
      axes: [
        {
          stroke: "#64748b",
          grid: { stroke: "#e2e8f0", width: 1 },
          ticks: { stroke: "#94a3b8", width: 1 },
          values: (_u, splits) => splits.map((v) => formatAxisTime(v)),
          rotate: -15,
          gap: 5,
          // Reserve enough height for the rotated date labels so they don't
          // overflow into the legend row below the chart (#108).
          size: 70,
        },
        {
          stroke: "#64748b",
          grid: { stroke: "#e2e8f0", width: 1 },
          ticks: { stroke: "#94a3b8", width: 1 },
          values: (_u, splits) => splits.map((v) => formatNumber(v)),
          label: valueLabel,
          labelSize: 20,
          labelGap: 8,
        },
      ],
      series: [
        {
          label: "UTC",
          value: (_u: uPlot, v: number | null) => v == null ? "--" : formatAxisTime(v),
        },
        {
          label: valueLabel,
          stroke: color,
          width: 1.5,
          fill: `${color}20`,
          points: {
            show: () => showPointsRef.current,
            size: pointSize,
            fill: color,
            stroke: color,
            width: 1,
          },
        },
      ],
    };

    const uplotData: uPlot.AlignedData = [timestamps, values];
    chartRef.current = new uPlot(opts, uplotData, container);

    // Handle resize
    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        if (chartRef.current) {
          chartRef.current.setSize({
            width: entry.contentRect.width,
            height: CHART_HEIGHT,
          });
        }
      }
    });

    resizeObserver.observe(container);

    return () => {
      resizeObserver.disconnect();
      if (chartRef.current) {
        chartRef.current.destroy();
        chartRef.current = null;
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [processedData, resolvedValueKey, unit, yDomain[0], yDomain[1], color, pointSize]);

  return { chartRef, handleResetZoom };
}
